/**
 * Self-Eval Leaderboard (Headerless)
 * 
 * Displays ionoff vs "best" using only self-evaluation grades
 * (where isSelfEval=true and isRetest=true)
 * 
 * This leaderboard is separate from peer-review leaderboards to avoid
 * conflating different evaluation methodologies.
 */

import React, { useMemo } from 'react';
import { ModelChip } from '../chat/ModelChip';
import { SelfEvalMajorityJudgmentData } from '../../hooks/useMajorityJudgment';
import { compareMajorityJudgments } from '../../services/majorityJudgmentService';

interface SelfEvalLeaderboardProps {
  judgedModels: SelfEvalMajorityJudgmentData[];
  tagColors: string[];
  modelColorAssignments: Map<string, string>;
  activeModels?: string[];  // Currently active models in header
  viewMode?: 'creator' | 'model';  // View mode for consistency (not used in self-eval)
  minJudgments?: number;  // Minimum judgments to display (default 3)
  prioritySetting?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom'; // Current priority setting
  currentUserId?: string; // NEW: Current user ID for "you" display
  maxChips?: number | null;  // Maximum chips to display (null = show all)
}

export const SelfEvalLeaderboard: React.FC<SelfEvalLeaderboardProps> = ({
  judgedModels,
  tagColors,
  modelColorAssignments,
  activeModels = [],
  viewMode = 'model',  // Default to model view
  minJudgments = 3,
  prioritySetting,
  currentUserId,
  maxChips = null,  // Default to showing all
}) => {
  // Prepare display data
  const displayData = useMemo(() => {
    console.log('🔍 SelfEvalLeaderboard: Input judgedModels', {
      count: judgedModels.length,
      modelIds: judgedModels.map(m => m.modelId),
      grades: judgedModels.map(m => m.majorityJudgment.medianGrade)
    });
    
    // First filter and map to display format
    const mapped = judgedModels
      .filter(item => {
        // Only show if meets minimum judgments threshold
        const judgmentsNumber = item.majorityJudgment.judgmentsNumber ?? 0;
        return judgmentsNumber >= minJudgments;
      })
      .map(item => {
        // Determine display name
        let displayName = item.modelId;
        
        // Replace "+human" modelId with "+human" display (global consolidated human responses)
        if (item.modelId === '+human') {
          displayName = '+human';
        } else if (currentUserId && item.modelId === currentUserId) {
          // Replace user ID with "+you" if it matches the current user
          displayName = '+you';
        } else if (item.modelId.startsWith('ionoff-grading-')) {
          // Versioned ionoff ID from backend (e.g., ionoff-grading-v1-synthesis-v1-new-plain)
          // Display as 'ionoff' so ModelChip renders the logo
          displayName = 'ionoff';
        } else if (item.modelId.startsWith('ionoff/ionoff-v')) {
          // Legacy versioned ionoff format - keep original modelId so ModelChip shows logo
          displayName = item.modelId;
        } else if (item.modelId.startsWith('ionoff_')) {
          // Synthesis variant - all display as "ionoff" (consistent with user request)
          // Previously we used to show ionoff_new_plain, ionoff_a_final, etc. as separate names
          // Now all synthesis variants display as "ionoff" (like the logo)
          displayName = 'ionoff';
        }
        
        // Determine color
        let backgroundColor: string;
        
        if (item.modelId === '+human') {
          // +human: use a distinct color (e.g., green for human)
          backgroundColor = '#22c55e'; // green-500
        } else if (item.ionoffEquals || item.modelId.startsWith('ionoff-grading-') || item.modelId.startsWith('ionoff/ionoff-v')) {
          // Final ionoff synthesis (displayed as ionoff version): use ionoff color
          const ionoffActive = activeModels.some(m => m.startsWith('ionoff'));
          backgroundColor = ionoffActive 
            ? (modelColorAssignments.get('ionoff') || tagColors[0] || '#FF6B6B')
            : '#9ca3af';
        } else if (item.modelId === 'best' || item.modelId === 'ionoff' || item.modelId.startsWith('ionoff_')) {
          // best, ionoff (renamed from ionoff_new_plain), AND synthesis variants: black background
          // This provides consistent styling between ionoff synthesis and the peer review winner
          backgroundColor = '#000000';
        } else {
          // Fallback for any other model IDs
          backgroundColor = '#9ca3af';
        }
        
        return {
          modelId: item.modelId,
          displayName,
          backgroundColor,
          majorityJudgment: item.majorityJudgment,
        };
      });
    
    // NOTE: Do NOT sort here! The judgedModels array already comes pre-sorted from the hook,
    // which preserves the correct order including the ionoffEquals relationship.
    // Re-sorting here would break ties incorrectly since we don't have response timestamps.
    
    // CRITICAL: Do NOT deduplicate by displayName if the items have different modelIds!
    // The hook already ensures proper ordering (e.g., ionoff_new_plain followed by ionoff-grading-...).
    // Deduplicating by displayName would break this ordering.
    // 
    // The only case where we might want deduplication is if:
    // - ionoff_new_plain has ionoffEquals=true (displays as ionoff/ionoff-v...) AND
    // - ionoff-grading-... (displays as ionoff)
    // But they should have different displayNames anyway (ionoff/ionoff-v... vs ionoff).
    // 
    // If both display as "ionoff", we want to keep both in the order the hook provided!
    const deduplicated = mapped; // NO deduplication - preserve the hook's ordering
    
    console.log('🔍 SelfEvalLeaderboard: Final order (preserved from hook)', {
      count: deduplicated.length,
      order: deduplicated.map(m => ({ 
        modelId: m.modelId, 
        displayName: m.displayName, 
        grade: m.majorityJudgment.medianGrade 
      }))
    });
    
    return deduplicated;
  }, [judgedModels, tagColors, modelColorAssignments, activeModels, minJudgments, prioritySetting]);

  // Don't render if no qualified data
  if (displayData.length === 0) {
    return null;
  }

  // Apply chip limit if specified
  const visibleData = maxChips !== null ? displayData.slice(0, maxChips) : displayData;

  return (
    <div className="space-y-0.5">
      {/* Headerless - just the chips */}
      <div className="px-3 space-y-0.5">
        {visibleData.map((item) => (
          <ModelChip
            key={item.modelId}
            modelId={item.modelId}
            displayName={item.displayName}
            isActive={false}
            backgroundColor={item.backgroundColor}
            onClick={() => {}}
            className="w-full justify-center text-center"
            majorityJudgment={{
              userJudgment: item.majorityJudgment,
              globalJudgment: null
            }}
            tagColors={tagColors}
            interactive={false}
          />
        ))}
      </div>
    </div>
  );
};


